/**
 * Browser replacements for the native modules the mobile app used:
 * expo-image-picker → <input type="file">
 * expo-audio        → MediaRecorder
 */

export type PickedFile = {
  file: File;
  /** Object URL for previewing — revoke with `releasePreview` when done. */
  url: string;
  kind: 'image' | 'video';
  mimeType: string;
  extension: string;
};

export function releasePreview(url: string | null | undefined) {
  if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
}

export function inferExtension(mimeType: string, kind: 'image' | 'video'): string {
  if (mimeType.includes('png')) return 'png';
  if (mimeType.includes('webp')) return 'webp';
  if (mimeType.includes('quicktime')) return 'mov';
  if (mimeType.includes('webm')) return 'webm';
  if (mimeType.includes('mp4') || kind === 'video') return 'mp4';
  return kind === 'image' ? 'jpg' : 'mp4';
}

/**
 * Opens the OS file picker. `source: 'camera'` asks mobile browsers for the
 * rear camera; desktop browsers fall back to a normal file dialog.
 * Resolves with `null` when the user cancels.
 */
export function pickFile(
  kind: 'image' | 'video',
  source: 'gallery' | 'camera' = 'gallery'
): Promise<PickedFile | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = kind === 'image' ? 'image/*' : 'video/*';
    if (source === 'camera') input.capture = 'environment';
    input.style.display = 'none';

    let settled = false;
    const finish = (value: PickedFile | null) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(value);
    };

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return finish(null);
      const mimeType = file.type || (kind === 'image' ? 'image/jpeg' : 'video/mp4');
      finish({
        file,
        url: URL.createObjectURL(file),
        kind,
        mimeType,
        extension: inferExtension(mimeType, kind),
      });
    };
    // `cancel` is not universally supported; the promise simply stays pending
    // in that case, which is harmless since the picker is re-openable.
    input.addEventListener('cancel', () => finish(null));

    document.body.appendChild(input);
    input.click();
  });
}

/**
 * Opens the OS file picker for several photos and/or videos at once. Each
 * file's kind comes from its own MIME type. Resolves with `[]` on cancel.
 */
export function pickMediaFiles(): Promise<PickedFile[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/*';
    input.multiple = true;
    input.style.display = 'none';

    let settled = false;
    const finish = (value: PickedFile[]) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(value);
    };

    input.onchange = () => {
      const files = Array.from(input.files ?? []);
      finish(
        files
          .filter((file) => file.type.startsWith('image/') || file.type.startsWith('video/'))
          .map((file) => {
            const kind = file.type.startsWith('video/') ? 'video' : 'image';
            return {
              file,
              url: URL.createObjectURL(file),
              kind,
              mimeType: file.type,
              extension: inferExtension(file.type, kind),
            };
          })
      );
    };
    input.addEventListener('cancel', () => finish([]));

    document.body.appendChild(input);
    input.click();
  });
}

/** Work media rows only store a URL, so videos are recognised by extension. */
export const isVideoUrl = (url: string) => /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url);

export type MicUnavailableReason = 'insecure-context' | 'unsupported' | null;

export function micUnavailableMessage(reason: MicUnavailableReason): string {
  if (reason === 'insecure-context') {
    return "L'enregistrement audio exige une connexion sécurisée (HTTPS). Ouvrez le site en https:// ou via localhost.";
  }
  return "Votre navigateur ne permet pas l'enregistrement audio.";
}

/** Turns a getUserMedia rejection into something the user can act on. */
export function micErrorMessage(error: unknown): string {
  const name = error instanceof DOMException ? error.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return "Accès au micro refusé. Autorisez-le dans les réglages du site (icône cadenas de la barre d'adresse).";
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'Aucun microphone détecté sur cet appareil.';
    case 'NotReadableError':
      return 'Le micro est déjà utilisé par une autre application.';
    default:
      return error instanceof Error
        ? error.message
        : "Impossible d'accéder au microphone.";
  }
}

/** Thin wrapper over MediaRecorder for chat / proposal voice notes. */
export class VoiceRecorder {
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private stream: MediaStream | null = null;

  static get isSupported() {
    return VoiceRecorder.unavailableReason() === null;
  }

  /**
   * Why recording can't start, or null when it can.
   *
   * `navigator.mediaDevices` is only exposed in a secure context, so the API is
   * missing entirely when the app is served over plain HTTP from anything other
   * than localhost — the usual cause on a LAN dev server or an http:// deploy.
   */
  static unavailableReason(): MicUnavailableReason {
    if (typeof MediaRecorder === 'undefined') return 'unsupported';
    if (!navigator.mediaDevices?.getUserMedia) {
      return window.isSecureContext ? 'unsupported' : 'insecure-context';
    }
    return null;
  }

  async start(): Promise<void> {
    const reason = VoiceRecorder.unavailableReason();
    if (reason) throw new Error(micUnavailableMessage(reason));
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.chunks = [];
    const recorder = new MediaRecorder(this.stream);
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    };
    recorder.start();
    this.recorder = recorder;
  }

  /** Stops recording and resolves with the recorded audio. */
  stop(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const recorder = this.recorder;
      if (!recorder) return reject(new Error('Aucun enregistrement en cours.'));
      recorder.onstop = () => {
        const blob = new Blob(this.chunks, { type: recorder.mimeType || 'audio/webm' });
        this.releaseStream();
        this.recorder = null;
        resolve(blob);
      };
      recorder.stop();
    });
  }

  /** Aborts an in-flight recording and frees the microphone. */
  cancel() {
    if (this.recorder && this.recorder.state !== 'inactive') {
      this.recorder.onstop = null;
      this.recorder.stop();
    }
    this.recorder = null;
    this.chunks = [];
    this.releaseStream();
  }

  private releaseStream() {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }
}

export const voiceExtensionFor = (mimeType: string) =>
  mimeType.includes('ogg') ? 'ogg' : mimeType.includes('mp4') ? 'm4a' : 'webm';
